import React, { PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";
import { css, SerializedStyles } from "@emotion/react";
import { withIsVisible } from "utils/reactUtils";
import { Text, TextSize } from "components/general/Text";
import { GlobalStore } from "store/global/GlobalStore";
import { ThemeProp } from "../../../styles/theme";
import styled from "@emotion/styled";

export type ColorType = "primary" | "secondary" | "quaternary" | "tertiary" | "link";

export enum ButtonBorderRadius {
  Large = "30px",
  Medium = "21px",
  Mid = "14px",
  Small = "8px",
  ExtraSmall = "4px",
}
export interface BaseButtonProps {
  buttonType?: ColorType;
  shape?: "round" | "square";
  height?: number;
  widthExpand?: boolean;
  width?: number;
  buttonCss?: SerializedStyles;
  padding?: `${string}px`;
  isDisabled?: boolean;
  hasActiveState?: boolean;
  isWaiting?: boolean;
  target?: string;
  rel?: string;
}
interface ButtonProps extends BaseButtonProps {
  navigateTo?: string;
  text?: string;
  textSize?: TextSize<"Button">;
}

export const Button = withIsVisible(
  ({
    navigateTo,
    text,
    textSize,
    buttonType,
    shape = "round",
    height,
    padding,
    widthExpand = false,
    width,
    buttonCss,
    children,
    isDisabled,
    isWaiting,
    ...props
  }: PropsWithChildren<ButtonProps & React.HTMLAttributes<HTMLButtonElement>>) => {
    const navigate = useNavigate();
    const theme = GlobalStore.useState(s => s.theme);

    return (
      <ButtonContainer
        onClick={e => {
          if (props.onClick) {
            props.onClick(e);
          }
          if (navigateTo?.startsWith("https")) {
            window.open(navigateTo, "_blank");
          } else if (navigateTo) {
            navigate(navigateTo);
          }
        }}
        buttonType={buttonType}
        shape={shape}
        height={height}
        widthExpand={widthExpand}
        padding={padding}
        width={width}
        css={buttonCss}
        theme={theme}
        disabled={isDisabled}
        isWaiting={isWaiting}
        {...props}
      >
        {children ? (
          children
        ) : (
          <Text textStyle="Button" size={textSize ?? "Large"}>
            {text}{" "}
          </Text>
        )}
      </ButtonContainer>
    );
  }
);

// Helpers

interface ButtonContainerProps extends BaseButtonProps, ThemeProp {}

const getBackgroundColorStates = (
  props: ButtonContainerProps
): { default: string; disabled: string; active: string; hover: string; waiting?: string } => {
  const { buttonType, theme } = props;
  if (buttonType && buttonType === "link") {
    return {
      default: "transparent",
      disabled: "transparent",
      active: "transparent",
      hover: "transparent",
    };
  }
  if (buttonType && theme.button[buttonType]?.surface) {
    return theme.button[buttonType].surface;
  }

  // Primary button colour fallback
  return theme.button.primary.surface;
};
export const getTextColorStates = (
  props: ButtonContainerProps
): { default: string; disabled: string; active: string; hover: string; waiting?: string } => {
  const { buttonType, theme } = props;
  if (buttonType && theme.button[buttonType]?.text) {
    return theme.button[buttonType].text;
  }

  // Primary button colour fallback
  return theme.button.primary.surface;
};

export const ButtonContainer = styled.button<ButtonContainerProps>(props => {
  const backgroundColorStates = getBackgroundColorStates(props);
  const textColorStates = getTextColorStates(props);
  const activeState = css`
    ${props.hasActiveState &&
    `
     backgroundColor: backgroundColorStates.active,
     color: textColorStates.active,
        
    &:active {
      transform: scale(0.95);
    }
  `}
  `;

  let width: string | number = "auto";
  if (props.widthExpand) {
    width = "100%";
  } else if (props.width) {
    width = props.width;
  }

  return {
    backgroundColor: backgroundColorStates.default,
    color: textColorStates.default,
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    padding: props.padding ?? "6px 18px",
    height: props.height ? `${props.height}px` : "auto",
    width,
    borderRadius: props.shape === "round" ? "21px" : "8px",

    transition: "background-color 0.2s linear, color 0.2s linear",
    outline: "none",

    "&:hover": {
      backgroundColor: backgroundColorStates.hover,
      color: textColorStates.hover,
    },

    "&:disabled": {
      backgroundColor: props.isWaiting
        ? backgroundColorStates.waiting
        : backgroundColorStates.disabled,
      color: props.isWaiting ? textColorStates.waiting : textColorStates.disabled,
    },
    activeState,
    ...props.buttonCss,
  };
});
